import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Skeleton, Typography } from "@mui/material";

import ConversationListPane from "./panes/ConversationListPane";
import MessageHistoryPane from "./panes/MessageHistoryPane";
import ConversationDetailsPane from "./panes/ConversationDetailsPane";
import { PERSONAL_WHATSAPP_CONVERSATION_PREFIX } from "../../api/client";

const ChatLayout = ({
  isMobile,
  containerStyle,
  userData,
  selectedConversationId,
  selectedConversation,
  showConversationList,
  showMessageHistory,
  showConversationDetails,
  isLoadingDetails,
  initialLoading,
  isLoadingConversation,
  messagesSyncError,
  reloadConversationMessages,
  messages,
  conversations,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  refetchConversations,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  onSelectConversation,
  onSendMessage,
  onEndConversation,
  onUpdateConversation,
  onBack,
  onShowDetails,
  onOpenPopup,
  onCloseConversationList,
  onNewConversation,
  isCreatingConversation,
  isError,
  aiInsights,
  isLoadingInsights,
  aiInsightsError,
  onLoadAiInsights,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onRefetchConversationDetails,
  onDeleteMessage,
  onDeletePersonalConversation,
}) => {
  const { t } = useTranslation("chat");

  const isPersonalWhatsappThread =
    Boolean(selectedConversation) &&
    (selectedConversation.kind === "personal" ||
      String(selectedConversation.id || "").startsWith(PERSONAL_WHATSAPP_CONVERSATION_PREFIX));
  const effectiveShowConversationDetails =
    Boolean(showConversationDetails && selectedConversation && !isPersonalWhatsappThread);

  if (initialLoading) {
    return (
      <Box display="flex" gap={1} height="98%" p={0}>
        <Skeleton variant="rectangular" width="25%" height="100%" sx={{ minWidth: 200, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">{t("layout.loadConversationsError")}</Typography>
      </Box>
    );
  }

  return (
    <Box
      className="tour-chat-layout"
      display="flex"
      p={0}
      gap={1}
      sx={{
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {showConversationList && (
        <ConversationListPane
          isMobile={isMobile}
          containerStyle={containerStyle}
          conversations={conversations}
          selectedConversation={selectedConversation}
          onSelectConversation={onSelectConversation}
          fetchNextPage={fetchNextPage}
          refetchConversations={refetchConversations}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isFetching={isFetching}
          initialLoading={initialLoading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          isLoadingDetails={isLoadingDetails}
          onClose={onCloseConversationList}
          onNewConversation={onNewConversation}
          isCreatingConversation={isCreatingConversation}
        />
      )}

      {showMessageHistory && (
        <MessageHistoryPane
          isMobile={isMobile}
          containerStyle={containerStyle}
          selectedConversationId={selectedConversationId}
          selectedConversation={selectedConversation}
          showConversationList={showConversationList}
          showConversationDetails={effectiveShowConversationDetails}
          userData={userData}
          isLoadingDetails={isLoadingDetails}
          initialLoading={initialLoading}
          isLoadingConversation={isLoadingConversation}
          messagesSyncError={messagesSyncError}
          reloadConversationMessages={reloadConversationMessages}
          messages={messages}
          onSendMessage={onSendMessage}
          onEndConversation={onEndConversation}
          onOpenPopup={onOpenPopup}
          onBack={onBack}
          onShowDetails={onShowDetails}
          onUpdateConversation={onUpdateConversation}
          onNewConversation={onNewConversation}
          onActivateConversation={onSelectConversation}
          hasMoreMessages={hasMoreMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          onLoadMoreMessages={onLoadMoreMessages}
          onRefetchConversationDetails={onRefetchConversationDetails}
          onDeleteMessage={onDeleteMessage}
          onDeletePersonalConversation={onDeletePersonalConversation}
        />
      )}

      {effectiveShowConversationDetails && selectedConversation && (
        <ConversationDetailsPane
          isMobile={isMobile}
          containerStyle={containerStyle}
          selectedConversation={selectedConversation}
          onClose={onShowDetails}
          onUpdateConversation={onUpdateConversation}
          aiInsights={aiInsights}
          isLoadingInsights={isLoadingInsights}
          aiInsightsError={aiInsightsError}
          onLoadAiInsights={onLoadAiInsights}
          onRefetchConversationDetails={onRefetchConversationDetails}
        />
      )}
    </Box>
  );
};

export default ChatLayout;
